import type { MealEstimate } from "../lib/types";
import type { Goals } from "../lib/goals";

interface Props {
  totals: MealEstimate["totals"];
  goals: Goals;
}

/**
 * 4-row macro display: Protein, Carbs (or Net Carbs), Fat, Fiber.
 *
 * Carbs label + value swap based on goals.track_net_carbs:
 *   off (default) → "Carbs" with total carbs (g)
 *   on            → "Net carbs" with (total carbs - fiber) g
 *
 * Fiber gets its own row always — it's a real macro people care about
 * (digestive health, satiety, micronutrient signal) and surfacing it
 * here makes the daily total visible without opening a meal.
 */
export default function MacroBars({ totals, goals }: Props) {
  const useNetCarbs = goals.track_net_carbs === true;
  const fiberValue = totals.fiber_g ?? 0;
  const carbValue = useNetCarbs
    ? Math.max(0, totals.carbs_g - fiberValue)
    : totals.carbs_g;
  const fiberGoal = goals.fiber_g_goal ?? 30;

  const rows = [
    {
      key: "protein",
      label: "Protein",
      color: "#38bdf8",
      value: totals.protein_g,
      goal: goals.protein_g
    },
    {
      key: "carbs",
      label: useNetCarbs ? "Net carbs" : "Carbs",
      color: "#fbbf24",
      value: carbValue,
      goal: goals.carbs_g
    },
    {
      key: "fat",
      label: "Fat",
      color: "#a78bfa",
      value: totals.fat_g,
      goal: goals.fat_g
    },
    {
      key: "fiber",
      label: "Fiber",
      color: "#34d399", // emerald-400 — distinct from the calorie ring's
                       //  emerald-500 but readable as "this is the good one"
      value: fiberValue,
      goal: fiberGoal
    }
  ];

  return (
    <div className="space-y-2.5">
      {rows.map((m) => {
        const pct = m.goal > 0 ? Math.min((m.value / m.goal) * 100, 100) : 0;
        const over = m.value > m.goal;
        return (
          <div key={m.key}>
            <div className="flex justify-between items-baseline text-xs mb-1.5">
              <span className="flex items-center gap-1.5 text-slate-300 font-medium">
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />
                {m.label}
              </span>
              <span className="tabular-nums">
                <span className="text-slate-100">{Math.round(m.value)}</span>
                <span className="text-slate-500"> / {Math.round(m.goal)}g</span>
              </span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  // Fiber rarely goes "over goal" in a bad way — going over
                  //  is genuinely positive for fiber. So we don't swap to
                  //  the rose-red color when over, just clamp at 100% bar.
                  backgroundColor: over && m.key !== "fiber" ? "#fb7185" : m.color
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
